import { Cell, Grid, Modal, ModalBody, ModalHeader, VFlow } from 'bold-ui'
import { useHistoricoResultadosExamesQuery } from 'graphql/hooks.generated'
import { TextFilterType } from 'hooks/filter/model'
import { useFilter } from 'hooks/filter/useFilter'
import { useMemo, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { maxBy, orderBy } from 'util/array'
import { toDate } from 'util/date/formatDate'
import { groupBy } from 'util/lang'
import { metaPath } from 'util/metaPath'
import { ResultadoExameQueryModel } from 'view/atendimentos/detail/soap/objetivo/resultados-exames/model-resultadosExames'

import {
  HistoricoResultadosExamesFilter,
  HistoricoResultadosExamesFilterModel,
} from './HistoricoResultadosExamesFilter'
import { HistoricoResultadosExamesItemModel, HistoricoResultadosExamesList } from './HistoricoResultadosExamesList'

interface HistoricoResultadosExamesModalProps {
  prontuarioId: ID
  editAtendProfId: ID
}

export const historicoResultadoExamesUrl = '/historico-de-resultados-de-exames'

export const HistoricoResultadosExamesModal = (props: HistoricoResultadosExamesModalProps) => {
  const { prontuarioId, editAtendProfId } = props

  const history = useHistory()
  const match = useRouteMatch()

  const [filter, setFilter] = useState<HistoricoResultadosExamesFilterModel>()

  const onClose = () => history.push(match.url.replace(historicoResultadoExamesUrl, ''))

  const {
    data: { resultadosExame },
    loading,
  } = useHistoricoResultadosExamesQuery({
    variables: {
      input: {
        prontuarioId,
        editAtendProfId,
        pageParams: { unpaged: true },
      },
    },
    fetchPolicy: 'cache-first',
  })

  const items = useMemo(
    () => (resultadosExame?.content as ResultadoExameQueryModel[])?.filter((item) => !!item.conferido) || [],
    [resultadosExame]
  )

  const groupedItems = useMemo(() => groupAndOrderItems(items), [items])

  const filteredItems = useFilter<HistoricoResultadosExamesItemModel, HistoricoResultadosExamesFilterModel>({
    items: groupedItems,
    filter,
    filtersType: [filterTextTypeConfig],
  })

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title='Histórico de resultados de exames' />
      <ModalBody>
        <VFlow vSpacing={2}>
          <Grid gapVertical={0.25}>
            <Cell size={5}>
              <HistoricoResultadosExamesFilter onChangeFilter={setFilter} />
            </Cell>

            <Cell size={12}>
              <HistoricoResultadosExamesList items={filteredItems} loading={loading} searchWords={filter?.texto} />
            </Cell>
          </Grid>
        </VFlow>
      </ModalBody>
    </Modal>
  )
}

const metaRow = metaPath<HistoricoResultadosExamesItemModel>()
const metaFilter = metaPath<HistoricoResultadosExamesFilterModel>()

const filterTextTypeConfig: TextFilterType = {
  filterField: metaFilter.texto,
  searchFields: [metaRow.exame.codigo, metaRow.exame.descricao],
  removeTagsOnFilter: true,
}

const groupAndOrderItems = (initialItems: ResultadoExameQueryModel[]): HistoricoResultadosExamesItemModel[] => {
  if (!initialItems) return []

  const itemsGrouped = groupBy(initialItems, (item) => item.exame.id)

  return orderBy(
    Object.entries(itemsGrouped).map(([id, items]) => ({
      id,
      exame: items[0].exame,
      items: orderBy(
        items,
        [(a) => getValueOfDataRealizacao(a), (b) => b.atendimentoProfissionalResultado.iniciadoEm],
        ['desc', 'desc']
      ),
    })),
    [
      (a) => getValueOfDataRealizacao(a.items[0]),
      (b) => maxBy(b.items, (c) => c.atendimentoProfissionalResultado.iniciadoEm),
    ],
    ['desc', 'desc']
  )
}

export const getValueOfDataRealizacao = ({ dataRealizacao }: ResultadoExameQueryModel) =>
  dataRealizacao ? toDate(dataRealizacao).valueOf() : ''
